import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {getFieldArrayFromObjectArrayByField, isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {aphQueueApi} from "./aphQueueApi";
import {orderRequestApi} from "../../../Order/OrderRequest/orderRequestApi";
import {STUDENT_APPROVAL_STATUS, STUDENT_STATUS} from "../../../Student/studentConstants";
import {createListFromArrayOfObjects} from "../../../../Utilities/Types/listUtilities";
import type { ListItem } from "../../../../Utilities/Types/listUtilities";
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullBleed} from "../../../../Components/Layout";
import {AphQueueForm} from "./AphQueueForm";
import {emptyStudentAphQueueModel} from "./aphQueueFactory";
import {useInputForm} from "../../../../Components/Inputs";
import {
    createConfirmationMessageForDuplicateAphCatalogItems,
    filterQueueByStatus, getAllDuplicateAphCatalogItemForStudent,
} from "./aphQueueUtility";
import {AphQueueList} from "./AphQueueList";
import {GridCell} from "../../../../Components/Grid";
import {SYSTEM_TYPES} from "../../libraryConstants";
import {getSystemTypeTextByValue} from "../../libraryUtility";

export const AphQueueContainer = ({
                                      handleApiCall,
                                      setWarnings,
                                      userId,
                                  }: ContextProps) => {

    const [aphQueue, setAphQueue] = useState<Record<string, unknown>[]>([]);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));
    const [students, setStudents] = useState<ListItem[]>();
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>();

    const aphLoanForm = useInputForm(emptyStudentAphQueueModel);

    const pendingQueue = filterQueueByStatus(aphQueue, true);
    const isPendingQueueEmpty = isArrayNullOrEmpty(pendingQueue);
    const processingQueue = filterQueueByStatus(aphQueue, false);

    const handleSelectAll = () => {
        const queueItems = getFieldArrayFromObjectArrayByField(pendingQueue, "aphQueueItemId");
        setSelectedItems(new Set(queueItems.map((id: unknown) => String(id))));
    }
    const handleDeselectAll = () => {
        setSelectedItems(new Set([]));    }

    const handleSelectedItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const item = event.target.value;
        const updatedSet = new Set(selectedItems || []);

        updatedSet.has(item)
            ? updatedSet.delete(item)
            : updatedSet.add(item);

        setSelectedItems(updatedSet);
    }

    const handleGetQueue = () => {
        handleApiCall(() => aphQueueApi.GetUserAphQueueItems(), (results: Record<string, unknown>[]) => setAphQueue(results));
    }

    const handleRemoveFromQueue = (aphQueuedItemId: string) => {
        if (confirm(`This will remove PERMANENTLY your request from the Queue. Are you sure you want to continue?`))
            handleApiCall(() => aphQueueApi.deleteAphQueueItem(aphQueuedItemId), handleGetQueue);
    }

    const handleAssignAphQueueItems = async (studentQueueViewModel: Record<string, unknown>) => {

        studentQueueViewModel.queueItemIds = Array.from(selectedItems);

        const noItemsSelectedFromPendingQueue = isArrayNullOrEmpty(studentQueueViewModel.queueItemIds);
        if(noItemsSelectedFromPendingQueue) {
            setWarnings(["Please select at least one item from the queue to assign to a student."]);
            return;
        }

        const duplicateAphCatalogItemForStudent = getAllDuplicateAphCatalogItemForStudent(studentQueueViewModel, pendingQueue, processingQueue);
        const hasDuplicateAphCatalogItem = duplicateAphCatalogItemForStudent.length > 0;
        const confirmationMessage = createConfirmationMessageForDuplicateAphCatalogItems(duplicateAphCatalogItemForStudent, hasDuplicateAphCatalogItem);

        if (!hasDuplicateAphCatalogItem || (hasDuplicateAphCatalogItem && confirm(confirmationMessage))) {
            handleApiCall(() => aphQueueApi.assignStudentToAphQueueItems(studentQueueViewModel), handleGetQueue);
            setSelectedItems(new Set([]));
        }
    }

    useEffect(() => {

        const getStudents = () => {
            handleApiCall(() => aphQueueApi.getStudents({
                userId: userId,
                isActive: true,
                studentApprovalStatus: STUDENT_APPROVAL_STATUS.Approved.id,
                studentStatus: STUDENT_STATUS.Active.id,
                HasSignedParentalConsent: true,
                forDropDown: true,
            }), (results: Record<string, unknown>[]) => {
                setStudents(createListFromArrayOfObjects(results, `studentId`, `fullName`));
            });
        };

        const getShippingLocations = () => {
            handleApiCall(() => orderRequestApi.getShippingLocations(), (results: Record<string, unknown>[]) => {
                setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
            });
        }

        if (!shippingLocations)
            getShippingLocations();
        if (!students)
            getStudents();

        handleGetQueue();
    }, []);

    if (!shippingLocations || !students || !aphLoanForm) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.APH.toString())} Queue`}/>

        <GridCell>
            {
                <p>
                    Please check with your school/organization to confirm that someone will be there to accept your
                    packages. Orders may take a month or more to arrive. All packages not received by your school or
                    organization will be returned to APH.
                </p>
            }
        </GridCell>

        <LayoutFullBleed className={`catalog`}>
            {
                pendingQueue &&
                <AphQueueList
                    aphQueue={pendingQueue}
                    handleDeselectAll={handleDeselectAll}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleSelectAll={handleSelectAll}
                    handleSelectedItemsChange={handleSelectedItemsChange}
                    selectedItems={selectedItems}
                />
            }
            {
                !isPendingQueueEmpty &&
                <AphQueueForm
                    aphLoanForm={aphLoanForm}
                    handleAssignAphQueueItems={handleAssignAphQueueItems}
                    setWarnings={setWarnings}
                    shippingLocations={shippingLocations}
                    students={students}
                />
            }
            {
                processingQueue &&
                <AphQueueList
                    aphQueue={processingQueue}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    isPendingQueue={false}
                />
            }
        </LayoutFullBleed>
    </>;
};
